import { createContext, useContext, type PropsWithChildren } from "react";
import type { MembershipCheckoutIntent } from "shared/utils/membershipIntent";

export type MembershipCheckoutIntentValue = {
  intent: MembershipCheckoutIntent;
  /** Storefront resource the intent applies to; others render as usual. */
  resourceId?: string | null;
  /** Checkout to open instead of the resource's own (the tier's mapped checkout). */
  bookerId?: string | null;
  /** Open the checkout immediately instead of showing past purchases first. */
  autoOpenBooker?: boolean;
  /** Called once the checkout has been auto-opened, so it isn't reopened on remount. */
  onBookerAutoOpened?: () => void;
};

const MembershipCheckoutIntentContext =
  createContext<MembershipCheckoutIntentValue | null>(null);

export const MembershipCheckoutIntentProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: MembershipCheckoutIntentValue | null }>) => (
  <MembershipCheckoutIntentContext.Provider value={value}>
    {children}
  </MembershipCheckoutIntentContext.Provider>
);

/**
 * The intent for `resourceId`, or undefined. A null `resourceId` on the
 * provider applies the intent to every storefront resource in the broadcast.
 */
export const useMembershipCheckoutIntent = (resourceId: string) => {
  const value = useContext(MembershipCheckoutIntentContext);
  if (!value) return undefined;
  if (value.resourceId && value.resourceId !== resourceId) return undefined;
  return value;
};
